import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import AvatarEditor from 'react-avatar-editor';

import { Empty, Button, Slider } from 'antd';
import { RotateRightOutlined, RotateLeftOutlined, UploadOutlined, UndoOutlined, DeleteFilled } from '@ant-design/icons';

import { txt } from '../../locales/i18n';
import { useDeviceSize } from '../../hooks/device-size';

export type ImageToolProps = {
	imageSource?: string;
	setImageSource: (imageSource: string) => void;
	refProxy: React.LegacyRef<AvatarEditor>;
	initialImageSource?: string;
	setInitialImageSource?: (imageSource: string) => void;
};

export function ImageTool(props: ImageToolProps): JSX.Element {
	const [initialEmpty, setInitialEmpty] = useState<boolean>(false);
	const [zoom, setZoom] = useState<number>(1);
	const [rotation, setRotation] = useState<number>(0);
	const inputRef: any = useRef(null);

	const isMobileDevice: boolean = useDeviceSize();

	useEffect(() => {
		if (initialEmpty === false) {
			setInitialEmpty(props.imageSource === undefined);
		}
	}, [props.imageSource, initialEmpty]);

	function setBase64ImageSource(): void {
		const reader: FileReader = new FileReader();
		const file = inputRef.current.files[0];

		reader.readAsDataURL(file);
		reader.onload = () => props.setImageSource(String(reader.result));
		setRotation(0);
		setZoom(1);
	}

	function handleResetImage(): void {
		setRotation(0);
		setZoom(1);
	}

	function handleReady(): void {
		const proxy = (props?.refProxy as any)?.current;
		if (proxy === null) return;

		setRotation(0);
		setZoom(1);

		if (props.initialImageSource === undefined) {
			const canvas = proxy.getImage();
			const initialBase64: string = canvas.toDataURL('image/jpeg');

			if (initialEmpty === false) {
				props.setInitialImageSource?.(initialBase64);
			}
		}
	}

	return (
		<Container>
			<div className="image-container">
				{Boolean(props.imageSource) === true && (
					<AvatarEditor
						ref={props.refProxy}
						image={props.imageSource ?? ''}
						width={200}
						height={200}
						border={25}
						color={[0, 0, 0, 0.6]}
						scale={zoom}
						rotate={rotation}
						borderRadius={200}
						onImageReady={handleReady}
					/>
				)}

				{Boolean(props.imageSource) === false && (
					<div style={{ height: '250px', width: '250px', display: 'grid', placeContent: 'center' }}>
						<Empty />
					</div>
				)}

				<div className="button-container">
					<Button icon={<RotateRightOutlined />} onClick={() => setRotation((previous) => previous + 90)}>
						{isMobileDevice === false && txt('image tool.rotate right')}
					</Button>

					<Button icon={<RotateLeftOutlined />} onClick={() => setRotation((previous) => previous - 90)}>
						{isMobileDevice === false && txt('image tool.rotate left')}
					</Button>

					<Button icon={<UploadOutlined />} onClick={() => inputRef.current.click()}>
						{isMobileDevice === false && 'Upload'}
					</Button>
					<input type="file" ref={inputRef} hidden onChange={setBase64ImageSource} accept="image/*" />

					<Button type="dashed" icon={<UndoOutlined />} onClick={handleResetImage}>
						{isMobileDevice === false && txt('global.reset')}
					</Button>

					<Button danger icon={<DeleteFilled />} onClick={() => props.setImageSource('')}>
						{isMobileDevice === false && txt('global.delete')}
					</Button>
				</div>
			</div>

			<Slider min={1} max={5} onChange={(value) => setZoom(value)} step={0.1} />
		</Container>
	);
}

const Container = styled.div`
	display: flex;
	width: fit-content;
	flex-direction: column;
	gap: 1rem;

	.image-container {
		display: flex;
	}

	.ant-slider {
		margin: 0.6rem;
	}

	.button-container {
		margin: 1rem;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}
`;
